import { auth, db } from '@/data/firebase/config';
import { ALLOWED_DOMAINS } from '@/domain/env/allowed-domains';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { User } from '@/domain/types/user';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import { validateAudienceSelection } from '@/domain/use-cases/audiences/membership-rules';
import AudienceSelect from '@/presentation/components/audience-select';
import Button from '@/presentation/components/button';
import { InputForm } from '@/presentation/components/inputs/input-form';
import { SelectForm } from '@/presentation/components/inputs/select-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaLock, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  Area,
  Department,
  Subarea,
  units,
} from '../../statistics/report-by-filter/env/filters';

interface AddUserFormProps {
  user?: User;
  isEdit?: boolean;
  onSaved?: () => void;
}

const userSchema = z.object({
  document: z
    .string({ message: 'El documento es requerido' })
    .min(6, { message: 'El documento debe tener al menos 6 caracteres' })
    .max(11, { message: 'El documento debe tener máximo 11 caracteres' })
    .regex(/^\d+$/, { message: 'El documento debe contener solo números' }),
  name: z
    .string({ message: 'El nombre es requerido' })
    .min(3, { message: 'El nombre debe tener al menos 3 caracteres' }),
  lastname: z
    .string({ message: 'El apellido es requerido' })
    .min(3, { message: 'El apellido debe tener al menos 3 caracteres' }),
  email: z
    .string({ message: 'El correo electrónico es requerido' })
    .email({ message: 'El correo electrónico no es válido' }),
  department: z
    .string({ message: 'El departamento es requerido' })
    .min(3, { message: 'El departamento debe tener al menos 3 caracteres' }),
  area: z
    .string({ message: 'La área es requerida' })
    .min(3, { message: 'La área debe tener al menos 3 caracteres' }),
  unit: z.string({ message: 'La unidad es requerida' }),
});

const AddUserForm = ({ user, isEdit, onSaved }: AddUserFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [subareas, setSubareas] = useState<Subarea[]>([]);
  const [audienceIds, setAudienceIds] = useState<string[]>(user?.audienceIds ?? []);
  const [audienceError, setAudienceError] = useState<string | undefined>();
  const { logCreate, logUpdate } = useAdminLogger();

  const methods = useForm<User>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: { ...user, isEnabled: user?.isEnabled || false },
  });

  useEffect(() => {
    methods.reset({ ...user, isEnabled: user?.isEnabled || false });
    setAudienceIds(user?.audienceIds ?? []);
    setAudienceError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveUser = async (data: User) => {
    setIsLoading(true);
    try {
      const docRef = doc(db, DOCUMENTS_DB.USERS, data.id);
      const isEnabled = methods.getValues('isEnabled').toString();

      const docData = {
        ...Object.fromEntries(
          Object.entries(data).map(([key, value]) => [
            key,
            typeof value === 'string' ? value.toLowerCase() : value,
          ]),
        ),
        role: 'employee',
        isEnabled: isEnabled === 'true',
        audienceIds,
      };

      await setDoc(docRef, docData);

      // Log the action
      await logCreate(
        DOCUMENTS_DB.USERS,
        data.id,
        `${data.name} ${data.lastname}`,
        docData,
      );

      toast.success('Empleado creado correctamente', { theme: 'colored' });
      onSaved?.();
      methods.reset();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      toast.error('Error al guardar empleado', { theme: 'colored' });
    } finally {
      setIsLoading(false);
    }
  };

  const createUserAuth = async (data: User) => {
    // Validar dominio permitido
    const email = data.email || '';
    const domain = email.split('@')[1] || '';
    if (!ALLOWED_DOMAINS.includes(domain)) {
      toast.error('El dominio del correo no está permitido', {
        theme: 'colored',
      });
      return;
    }
    try {
      const collectionRef = collection(db, DOCUMENTS_DB.USERS);
      const queryRef = query(
        collectionRef,
        where('document', '==', data.document),
      );
      const querySnapshot = await getDocs(queryRef);

      if (querySnapshot.size > 0) {
        toast.error('El documento ya está registrado', { theme: 'colored' });
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.document,
      );
      saveUser({ ...data, id: userCredential.user.uid });
    } catch (error) {
      toast.error('Error al crear usuario en auth', { theme: 'colored' });
    }
  };

  const updateUser = async (data: User) => {
    console.log('data', data);
    console.log('user', user);
    setIsLoading(true);
    try {
      const docRef = doc(db, DOCUMENTS_DB.USERS, user?.id || '');

      const isEnabled = methods.getValues('isEnabled').toString();

      const docData = {
        ...data,
        role: 'employee',
        isEnabled: isEnabled === 'true',
        audienceIds,
      };

      await updateDoc(docRef, docData);

      // Log the action
      await logUpdate(
        DOCUMENTS_DB.USERS,
        user?.id || '',
        `${data.name} ${data.lastname}`,
        docData,
      );

      toast.success('Empleado actualizado correctamente', { theme: 'colored' });
      onSaved?.();
      methods.reset();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      toast.error('Error al actualizar empleado', { theme: 'colored' });
    } finally {
      setIsLoading(false);
    }
  };

  // const handleDeleteUser = async () => {
  //   if (!user?.document) return;

  //   try {
  //     const docRef = doc(db, DOCUMENTS_DB.USERS, user.id);
  //     await deleteDoc(docRef);

  //     toast.success('Empleado eliminado correctamente', { theme: 'colored' });
  //     onSaved?.();
  //   } catch (error) {
  //     console.error('Error al eliminar usuario:', error);
  //     toast.error('Error al eliminar empleado', { theme: 'colored' });
  //   }
  // };

  const onSubmit = (data: User) => {
    const validation = validateAudienceSelection(audienceIds);
    if (!validation.valid) {
      setAudienceError(validation.message);
      return;
    }
    setAudienceError(undefined);

    if (isEdit) {
      updateUser(data);
    } else {
      createUserAuth(data);
    }
  };

  const handleResetPassword = async () => {
    try {
      if (user?.email) {
        await sendPasswordResetEmail(auth, user?.email);
        toast.success(
          'Se ha enviado un correo para restablecer la contraseña',
          {
            theme: 'colored',
          },
        );
      } else {
        toast.error('No se puede restablecer la contraseña', {
          theme: 'colored',
        });
      }
    } catch (error) {
      console.error('Error al restablecer contraseña:', error);
      toast.error('Error al restablecer contraseña', { theme: 'colored' });
    }
  };

  return (
    <div className="flex flex-col gap-2 p-4 justify-between h-5/6">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2">
            <InputForm
              label="Documento"
              name="document"
              placeholder="Documento"
              disabled={isEdit}
            />
            <InputForm label="Nombre" name="name" placeholder="Nombre" />
            <InputForm
              label="Apellido"
              name="lastname"
              placeholder="Apellido"
            />
            <InputForm
              label="Correo electrónico"
              name="email"
              placeholder="Correo electrónico"
              disabled={isEdit}
            />
            <SelectForm
              label="Unidad"
              name="unit"
              placeholder="Unidad"
              options={units
                .sort((a, b) => a.unit.localeCompare(b.unit))
                .map((unit) => ({
                  value: unit.unit,
                  label: unit.unit,
                }))}
              onChange={(value) => {
                setAreas([]);
                setDepartments([]);
                setDepartments(
                  units.find((unit) => unit.unit === value)?.departments || [],
                );
              }}
            />
            <SelectForm
              label="Departamento"
              name="department"
              placeholder="Departamento"
              options={departments
                .sort((a, b) => a.department.localeCompare(b.department))
                .map((department) => ({
                  value: department.department,
                  label: department.department,
                }))}
              onChange={(value) => {
                setAreas([]);
                setAreas(
                  departments.find(
                    (department) => department.department === value,
                  )?.areas || [],
                );
              }}
            />
            <SelectForm
              label="Área"
              name="area"
              placeholder="Área"
              options={areas
                .sort((a, b) => a.area.localeCompare(b.area))
                .map((area) => ({
                  value: area.area,
                  label: area.area,
                }))}
              onChange={(value) => {
                setSubareas([]);
                setSubareas(
                  areas.find((area) => area.area === value)?.subareas || [],
                );
              }}
            />
            <SelectForm
              label="Cargo"
              name="position"
              placeholder="Cargo"
              options={subareas
                .sort((a, b) => a.subarea.localeCompare(b.subarea))
                .map((subarea) => ({
                  value: subarea.subarea,
                  label: subarea.subarea,
                }))}
            />

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Audiencias</label>
              <AudienceSelect
                value={audienceIds}
                onChange={setAudienceIds}
                emptyLabel="Sin audiencias: el usuario pertenece solo a la audiencia general."
                helpText="Las audiencias permiten dirigir una publicación a un grupo específico de usuarios. Un usuario ve siempre las publicaciones generales; si pertenece a una audiencia, ve además las publicaciones dirigidas a ella. Si no seleccionas ninguna, el usuario pertenece únicamente a la audiencia general."
                error={audienceError}
              />
            </div>

            <SelectForm
              label="Estado"
              name="isEnabled"
              options={[
                { value: 'true', label: 'Habilitado' },
                { value: 'false', label: 'Inhabilitado' },
              ]}
            />
            <Button
              icon={<FaSave />}
              label="Guardar"
              type="submit"
              isLoading={isLoading}
            />
          </div>
        </form>
      </FormProvider>

      <div className="flex flex-col gap-2">
        <Button
          label="Restablecer contraseña"
          variant="secondary"
          onClick={handleResetPassword}
          icon={<FaLock />}
        />
        {/* <Button
          label="Eliminar empleado"
          variant="ghost"
          onClick={handleDeleteUser}
          icon={<FaTrash />}
        /> */}
      </div>
    </div>
  );
};

export default AddUserForm;
